import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { taskStatus, TASK_STATUS_COLORS } from "@/lib/taskStatus";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Plus, CloudRain, StickyNote, Loader2, GanttChartSquare } from "lucide-react";
import { TaskDialog, DelayDialog } from "@/components/ProjectView";

const DAY = 86400000;
const parse = (s) => new Date(`${String(s).slice(0, 10)}T00:00:00`);
const fmtD = (s) => (s ? parse(s).toLocaleDateString("en-AU", { day: "numeric", month: "short" }) : "—");
const fmtTick = (d) => d.toLocaleDateString("en-AU", { day: "numeric", month: "short" });

export default function JobInstallPlan({ job }) {
  const nav = useNavigate();
  const [taskDraft, setTaskDraft] = useState(null);
  const [delayDraft, setDelayDraft] = useState(null);

  const { data: plan, isLoading } = useQuery({
    queryKey: ["project-plan", job.id],
    queryFn: async () => (await api.get(`/project/plan?include_job=${job.id}`)).data,
  });
  const { data: team = [] } = useQuery({ queryKey: ["team"], queryFn: async () => (await api.get("/auth/team")).data });

  const jobs = useMemo(() => plan?.jobs || [], [plan]);
  const me = jobs.find((j) => j.id === job.id);
  const canEdit = !!plan?.can_edit && !me?.inactive;
  const tasks = useMemo(
    () => [...(me?.tasks || [])].sort((a, b) => (a.start || "").localeCompare(b.start || "") || (a.order || 0) - (b.order || 0)),
    [me]
  );
  const delays = useMemo(() => me?.delays || [], [me]);

  const scale = useMemo(() => {
    const ds = [...tasks.flatMap((t) => [t.start, t.finish]), ...delays.flatMap((d) => (d.dates?.length ? d.dates : [d.start]))]
      .filter(Boolean).map((s) => parse(s).getTime());
    if (!ds.length) return null;
    const min = Math.min(...ds), max = Math.max(...ds);
    const span = Math.max(1, Math.round((max - min) / DAY) + 1);
    const step = span <= 14 ? 1 : span <= 45 ? 7 : Math.ceil(span / 8);
    const ticks = [];
    for (let i = 0; i < span; i += step) ticks.push({ pct: (i / span) * 100, label: fmtTick(new Date(min + i * DAY)) });
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const todayPct = today >= min && today <= max ? (((today - min) / DAY + 0.5) / span) * 100 : null;
    const pos = (s, e) => {
      const a = Math.round((parse(s).getTime() - min) / DAY);
      const b = Math.round((parse(e).getTime() - min) / DAY);
      return { left: (a / span) * 100, width: Math.max(1.2, ((b - a + 1) / span) * 100) };
    };
    return { ticks, todayPct, pos };
  }, [tasks, delays]);

  const openPlanner = () => { localStorage.setItem("scheduler-mode", "project"); nav("/scheduler"); };

  return (
    <Card className="rounded-sm border-slate-200 industrial-shadow" data-testid="job-install-plan">
      <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="stamp-label text-slate-500">Project Plan</div>
          <h3 className="font-display text-lg font-semibold mt-0.5">Install timeline</h3>
          <p className="text-[11px] text-slate-400 mt-0.5">Live from the Scheduler's Project planner — same tasks, one source of truth</p>
        </div>
        <div className="flex gap-2 shrink-0 flex-wrap">
          {canEdit && me && (
            <>
              <Button size="sm" variant="outline" onClick={() => setTaskDraft({ jobId: job.id, task: null })} className="rounded-sm" data-testid="job-plan-add-task-btn">
                <Plus size={14} className="mr-1" /> Task
              </Button>
              <Button size="sm" variant="outline" onClick={() => setDelayDraft({ jobId: job.id, delay: null })} className="rounded-sm border-amber-300 text-amber-700 hover:bg-amber-50" data-testid="job-plan-add-delay-btn">
                <CloudRain size={14} className="mr-1" /> Delay
              </Button>
            </>
          )}
          <Button size="sm" onClick={openPlanner} className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-white" data-testid="job-plan-open-planner-btn">
            <GanttChartSquare size={14} className="mr-1" /> Open planner
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="p-8 flex items-center justify-center gap-2 text-sm text-slate-400"><Loader2 size={15} className="animate-spin" /> Loading plan…</div>
      ) : tasks.length === 0 && delays.length === 0 ? (
        <div className="p-8 text-center text-sm text-slate-500" data-testid="job-plan-empty">
          No install plan yet — {canEdit ? (
            <>add the <button onClick={() => setTaskDraft({ jobId: job.id, task: null })} className="text-cyan-700 font-semibold hover:underline" data-testid="job-plan-add-first-task-btn">first task</button> here, or build it in the Scheduler's Project view.</>
          ) : ("the plan is built in the Scheduler's Project view.")}
        </div>
      ) : (
        <div className="p-4 overflow-x-auto">
          <div className="min-w-[560px]">
            <div className="grid grid-cols-[190px_1fr] gap-2 mb-1">
              <div />
              <div className="relative h-5 border-b border-slate-200">
                {scale?.ticks.map((t, i) => (
                  <span key={i} className="absolute top-0 text-[9px] font-mono text-slate-400 -translate-x-1/2 whitespace-nowrap" style={{ left: `${t.pct}%` }}>{t.label}</span>
                ))}
              </div>
            </div>
            <div className="space-y-1.5">
              {tasks.map((t) => {
                const filledDays = Math.round((t.duration || 1) * ((t.percent || 0) / 100));
                const st = taskStatus(t);
                const stc = TASK_STATUS_COLORS[st];
                let consumed = 0;
                return (
                  <div key={t.id} className="grid grid-cols-[190px_1fr] gap-2 items-center" data-testid={`job-plan-task-row-${t.id}`}>
                    <button onClick={() => setTaskDraft({ jobId: job.id, task: t })} className="text-left min-w-0 group" data-testid={`job-plan-task-open-${t.id}`}>
                      <div className={`text-xs font-medium truncate group-hover:text-cyan-700 ${t.percent === 100 ? "text-slate-400 line-through" : "text-slate-800"}`}>
                        <span className="font-mono text-[9px] text-slate-400 mr-1">{t.task_no}</span>{t.name}
                        {st === "late" && <span className="ml-1.5 text-[9px] font-extrabold text-rose-600" data-testid={`job-plan-overdue-${t.id}`}>● OVERDUE</span>}
                      </div>
                      <div className="text-[10px] text-slate-400 truncate pl-0.5 flex items-center gap-1">
                        {fmtD(t.start)} → {fmtD(t.finish)} · {t.duration}d{t.percent > 0 ? ` · ${t.percent}%` : ""}
                        {t.notes && <StickyNote size={9} className="shrink-0 text-amber-500" />}
                      </div>
                    </button>
                    <div className="relative h-7 bg-slate-50 border border-slate-100 rounded-sm">
                      {scale?.todayPct != null && <div className="absolute top-0 bottom-0 w-px bg-rose-400 z-10 pointer-events-none" style={{ left: `${scale.todayPct}%` }} />}
                      {(t.segments || []).map((seg, si) => {
                        const p = scale.pos(seg.start, seg.end);
                        const segLen = Math.round((parse(seg.end) - parse(seg.start)) / DAY) + 1;
                        const fillHere = Math.max(0, Math.min(filledDays - consumed, segLen));
                        consumed += segLen;
                        return (
                          <button key={si} onClick={() => setTaskDraft({ jobId: job.id, task: t })}
                            className="absolute top-1 bottom-1 rounded-[3px] overflow-hidden hover:ring-2 hover:ring-cyan-400"
                            style={{ left: `${p.left}%`, width: `${Math.min(p.width, 100 - p.left)}%`, background: stc.base }}
                            title={`${t.name} — ${stc.label}: ${t.start} → ${t.finish}${t.notes ? `\n${t.notes}` : ""}`}
                            data-testid={si === 0 ? `job-plan-task-bar-${t.id}` : undefined}>
                            {fillHere > 0 && <span className="absolute left-0 top-0 bottom-0" style={{ width: `${(fillHere / segLen) * 100}%`, background: stc.fill }} />}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
              {delays.map((d) => {
                const p = scale ? scale.pos(d.dates?.[0] || d.start, d.dates?.[d.dates.length - 1] || d.start) : null;
                return (
                  <div key={d.id} className="grid grid-cols-[190px_1fr] gap-2 items-center" data-testid={`job-plan-delay-row-${d.id}`}>
                    <button onClick={() => setDelayDraft({ jobId: job.id, delay: d })} className="text-left min-w-0 group">
                      <div className="text-xs font-medium text-amber-800 truncate group-hover:text-amber-600 flex items-center gap-1">
                        <CloudRain size={10} className="shrink-0" /> Delay — {d.reason}
                      </div>
                      <div className="text-[10px] text-amber-600/70 truncate">{fmtD(d.start)} · {d.days}d lost</div>
                    </button>
                    <div className="relative h-7 bg-amber-50/40 border border-amber-100 rounded-sm">
                      {scale?.todayPct != null && <div className="absolute top-0 bottom-0 w-px bg-rose-400 z-10 pointer-events-none" style={{ left: `${scale.todayPct}%` }} />}
                      {p && (
                        <button onClick={() => setDelayDraft({ jobId: job.id, delay: d })}
                          className="absolute top-1 bottom-1 rounded-[3px] border border-dashed border-amber-600"
                          style={{ left: `${p.left}%`, width: `${Math.min(p.width, 100 - p.left)}%`, background: "repeating-linear-gradient(45deg,#fcd34d 0 5px,#f8b84e 5px 10px)" }}
                          title={`${d.reason} — ${d.days}d`} data-testid={`job-plan-delay-bar-${d.id}`} />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex flex-wrap items-center gap-3 mt-3 pt-2 border-t border-slate-100 text-[10px] text-slate-500">
              <span className="font-mono" data-testid="job-plan-range">{fmtD(me?.start)} → {fmtD(me?.finish)}</span>
              <span>· {tasks.length} task{tasks.length !== 1 ? "s" : ""}{delays.length ? ` · ${delays.length} delay${delays.length > 1 ? "s" : ""}` : ""}</span>
              <span className="flex items-center gap-1 ml-auto"><span className="inline-block w-3 h-2 rounded-[2px] bg-[#8ce0b4]" /> Completed</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-2 rounded-[2px] bg-[#fcd34d]" /> In progress</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-2 rounded-[2px] bg-[#7fb3dd]" /> Future</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-2 rounded-[2px] bg-[#fda4af]" /> Overdue</span>
              <span className="flex items-center gap-1"><span className="inline-block w-3 h-2 rounded-[2px]" style={{ background: "linear-gradient(90deg,#2f6ea5 0 50%,#7fb3dd 50% 100%)" }} /> % done fill</span>
              {delays.length > 0 && <span className="flex items-center gap-1"><span className="inline-block w-3 h-2 rounded-[2px]" style={{ background: "repeating-linear-gradient(45deg,#fcd34d 0 4px,#f8b84e 4px 8px)" }} /> Delay</span>}
              {scale?.todayPct != null && <span className="flex items-center gap-1 text-rose-500"><span className="w-px h-3 bg-rose-400 inline-block" /> Today</span>}
            </div>
          </div>
        </div>
      )}

      <TaskDialog draft={taskDraft} jobs={jobs} team={team} canEdit={canEdit}
        onClose={() => setTaskDraft(null)}
        onAddDelay={(jid) => { setTaskDraft(null); setDelayDraft({ jobId: jid, delay: null }); }} />
      <DelayDialog draft={delayDraft} jobs={jobs} canEdit={canEdit} onClose={() => setDelayDraft(null)} />
    </Card>
  );
}
